/**
 * The small parts an item's details are built from: a card per concern with
 * its own pencil, a fact in a card's read mode, a row of a card's form, and
 * a failed save's message.
 */

import { Pencil } from "lucide-react";
import { type ReactNode, type RefObject, useEffect, useId, useRef } from "react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { RoadmapOverview } from "@/lib/api";
import { cn } from "@/lib/utils";

/** What the details read of the roadmap: who is looking, every item and the people. */
export type DetailsContext = Pick<RoadmapOverview, "items" | "people" | "viewer">;

interface EditPencilProps {
  /** What it edits, its name and its tooltip: "Planning bewerken". */
  label: string;
  onClick: () => void;
  /** For a pencil that toggles a mode (the blockages' Verwijderen): pressed while on. */
  pressed?: boolean;
  ref?: RefObject<HTMLButtonElement | null>;
}

/** A card's small pencil, with what it edits in a tooltip. */
export function EditPencil({ label, onClick, pressed, ref }: EditPencilProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={ref}
          variant="ghost"
          size="icon"
          aria-label={label}
          aria-pressed={pressed}
          onClick={onClick}
          className={cn(
            "text-muted size-8 shrink-0 pointer-coarse:size-10",
            pressed && "bg-accent-light text-primary",
          )}
        >
          <Pencil aria-hidden="true" className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The focus back on a card's pencil once its editor closes, so the keyboard
 * never lands on the page's body. Returns the ref for the pencil.
 */
export function useFocusBack(editing: boolean) {
  const pencil = useRef<HTMLButtonElement>(null);
  const was = useRef(editing);
  useEffect(() => {
    if (was.current && !editing) {
      pencil.current?.focus();
    }
    was.current = editing;
  }, [editing]);
  return pencil;
}

/**
 * Whether the keyboard focus fell off an element that just left the page: it
 * is on the page's body, or on the dialog itself, where Radix's focus trap
 * catches it. Then the details may put it somewhere useful; otherwise it
 * stays where the viewer put it.
 */
export function focusFell(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  const active = document.activeElement;
  return active === null || active === document.body || active.getAttribute("role") === "dialog";
}

interface DetailsCardProps {
  title: string;
  /** Muted, after the title: a count. */
  note?: ReactNode;
  /** On the header's right, before the pencil: a button such as Volgen. */
  aside?: ReactNode;
  /** The pencil, when the card edits. */
  pencil?: ReactNode;
  /** The card's body; none for a card that is its header alone (no blockages yet). */
  children?: ReactNode;
  /** Its `data-section`, for tests and the page's own checks. */
  section: string;
}

/** One concern of the item, with its own header and pencil. */
export function DetailsCard({ title, note, aside, pencil, children, section }: DetailsCardProps) {
  const id = useId();
  return (
    <section aria-labelledby={id} data-section={section} className="bg-card rounded-md border">
      <header className={cn("flex h-11 items-center gap-2 px-3", children && "border-b")}>
        <h3 id={id} className="text-foreground text-sm font-semibold">
          {title}
          {note !== undefined && (
            <span className="text-muted ml-1 font-normal tabular-nums">{note}</span>
          )}
        </h3>
        <div className="-mr-1 ml-auto flex items-center gap-1">
          {aside}
          {pencil}
        </div>
      </header>
      {children && <div className="p-3">{children}</div>}
    </section>
  );
}

interface FactProps {
  label: string;
  /** Its `data-fact`, for tests. */
  name: string;
  children: ReactNode;
}

/** A fact in a card's read mode: its label in the same column as the form's, and its value. */
export function Fact({ label, name, children }: FactProps) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-muted w-24 shrink-0">{label}</dt>
      <dd className="min-w-0" data-fact={name}>
        {children}
      </dd>
    </div>
  );
}

interface FormRowProps {
  label: string;
  /** The control the label names; none for a value shown as text. */
  htmlFor?: string;
  children: ReactNode;
}

/** A label and a value in a card's form, side by side from a tablet up, stacked on a phone. */
export function FormRow({ label, htmlFor, children }: FormRowProps) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-muted w-24 shrink-0 text-sm">
          {label}
        </label>
      ) : (
        <span className="text-muted w-24 shrink-0 text-sm">{label}</span>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** A failed save's message, announced as it appears. */
export function Failure({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="text-destructive text-sm">
      {children}
    </p>
  );
}
